import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { TopBar } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "", category: "Destaques", image_url: "", available: true };

export default function MerchantMenu() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/merchant/menu").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) await api.put(`/merchant/menu/${editing}`, payload);
      else await api.post("/merchant/menu", payload);
      toast.success(editing ? "Item atualizado" : "Item adicionado");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggle = async (item) => {
    await api.put(`/merchant/menu/${item.id}`, { ...item, available: !item.available });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/merchant/menu/${id}`);
    toast.success("Item removido");
    load();
  };

  return (
    <div data-testid="merchant-menu-page">
      <TopBar subtitle="Gestão de cardápio" />
      <div className="px-4 pt-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold">Cardápio</h1>
          <Button
            data-testid="add-menu-item-button"
            onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}
            className="h-10 rounded-xl gf-gradient text-white font-bold gf-press"
          >
            <Plus size={16} className="mr-1" /> Novo item
          </Button>
        </div>

        <div className="grid gap-2 mt-4 gf-stagger" data-testid="merchant-menu-list">
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 p-3 rounded-2xl border border-gray-200 bg-white items-center">
              {i.image_url && <img src={i.image_url} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{i.name}</p>
                <p className="text-[11px] text-gray-500 truncate">{i.category} · {brl(i.price)}</p>
                <p className="text-[10px] text-gray-400 truncate">{i.description}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Switch
                  data-testid={`toggle-item-${i.id}`}
                  checked={i.available}
                  onCheckedChange={() => toggle(i)}
                />
                <div className="flex gap-1">
                  <button
                    data-testid={`edit-item-${i.id}`}
                    onClick={() => { setForm({ ...i, price: String(i.price) }); setEditing(i.id); setOpen(true); }}
                    className="p-1.5 gf-press"
                  >
                    <Pencil size={14} className="text-gray-500" />
                  </button>
                  <button data-testid={`delete-item-${i.id}`} onClick={() => remove(i.id)} className="p-1.5 gf-press">
                    <Trash2 size={14} className="text-mage-red" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px] rounded-2xl" data-testid="menu-item-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar item" : "Novo item"}</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <Input data-testid="item-name-input" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
            <Textarea data-testid="item-description-input" placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" />
            <Input data-testid="item-price-input" placeholder="Preço (ex: 26.90)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="item-category-input" placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="item-image-input" placeholder="URL da foto" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="h-11 rounded-xl" />
            <Button data-testid="save-menu-item-button" onClick={save} className="h-11 rounded-xl gf-gradient text-white font-bold">
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
